import type { DeviceButtonPayload } from '../types/device.types'

export const BUTTON_FUNCTIONS_USING_EXTENSION = new Set([
  'line',
  'presence',
  'voicemail',
])

const LEGACY_DEFAULT_MAIN_BUTTONS: DeviceButtonPayload[] = [
  {
    type: 'LineButton',
    display_count: 1,
    button_function: 'line',
    button_label: 'Line 1',
  },
  {
    type: 'LineButton',
    display_count: 2,
    button_function: 'rollover',
  },
  {
    type: 'LineButton',
    display_count: 3,
    button_function: 'parking_slot',
    button_value: '5901',
    slot_number: 5901,
    button_label: 'P1',
  },
  {
    type: 'LineButton',
    display_count: 4,
    button_function: 'parking_slot',
    button_value: '5902',
    slot_number: 5902,
    button_label: 'P2',
  },
]

export const getLegacyDefaultMainButtons = (): DeviceButtonPayload[] =>
  LEGACY_DEFAULT_MAIN_BUTTONS.map((button) => ({ ...button }))

export function sanitizeButton(
  button: DeviceButtonPayload,
  primaryExtensionId?: number,
): DeviceButtonPayload | null {
  const buttonFunction = button.button_function?.trim()
  if (!buttonFunction) return null

  const base: DeviceButtonPayload = {
    type: button.type,
    display_count: button.display_count,
    button_function: buttonFunction,
  }

  // Always pass through the label, even when empty, so a user clearing the
  // label field on a saved button actually overwrites the stored value
  // instead of being silently stripped from the payload.
  if (button.button_label !== undefined) {
    base.button_label = button.button_label.trim()
  }

  if (BUTTON_FUNCTIONS_USING_EXTENSION.has(buttonFunction)) {
    const extensionId =
      button.type === 'LineButton' && button.display_count === 1
        ? primaryExtensionId
        : (button.extension ?? undefined)
    if (!extensionId) return null

    base.extension = extensionId
    base.button_value = String(extensionId)
    return base
  }

  if (buttonFunction === 'speed_dial' || buttonFunction === 'dtmf') {
    const speedDialValue = button.speed_dial_value?.trim()
    if (!speedDialValue) return null

    const extensionId = button.extension ?? primaryExtensionId
    if (!extensionId) return null

    base.extension = extensionId
    base.button_value = speedDialValue
    base.speed_dial_value = speedDialValue
    return base
  }

  if (buttonFunction === 'parking_slot') {
    const parkingValue = button.button_value?.trim()
    if (!parkingValue) return null

    const extensionId = button.extension ?? primaryExtensionId
    if (!extensionId) return null

    const parkingNum = Number(parkingValue)
    base.extension = extensionId
    base.button_value = parkingValue
    base.slot_number =
      button.slot_number ??
      (Number.isFinite(parkingNum) ? parkingNum : undefined)
    return base
  }

  if (buttonFunction === 'rollover') {
    const extensionId = button.extension ?? primaryExtensionId
    if (!extensionId) return null

    base.extension = extensionId
    base.button_value = button.button_value?.trim() || String(extensionId)
    return base
  }

  return null
}

export function sanitizeDeviceButtonsForApi(
  source: DeviceButtonPayload[],
  options?: {
    primaryExtensionId?: number
    includePrimaryLineFallback?: boolean
    includeSidecarButtons?: boolean
    maxLineButtons?: number
  },
): DeviceButtonPayload[] {
  const primaryExtensionId = options?.primaryExtensionId
  const includeSidecarButtons = options?.includeSidecarButtons ?? true
  const normalizedMaxLineButtons =
    options?.maxLineButtons != null &&
    Number.isFinite(options.maxLineButtons) &&
    options.maxLineButtons > 0
      ? options.maxLineButtons
      : null
  const sanitized = source
    .filter((button) =>
      normalizedMaxLineButtons != null
        ? button.type !== 'LineButton' ||
          button.display_count <= normalizedMaxLineButtons
        : true,
    )
    .filter((button) =>
      includeSidecarButtons ? true : button.type !== 'SidecarButton',
    )
    .map((button) => sanitizeButton(button, primaryExtensionId))
    .filter((button): button is DeviceButtonPayload => Boolean(button))

  const hasPrimaryLineButton = sanitized.some(
    (button) =>
      button.type === 'LineButton' &&
      button.display_count === 1 &&
      button.button_function === 'line',
  )

  if (
    options?.includePrimaryLineFallback &&
    primaryExtensionId &&
    !hasPrimaryLineButton
  ) {
    sanitized.unshift({
      type: 'LineButton',
      display_count: 1,
      button_function: 'line',
      extension: primaryExtensionId,
      button_value: String(primaryExtensionId),
    })
  }

  return sanitized
}

export function hasInvalidDeviceButtonConfiguration(
  source: DeviceButtonPayload[],
  options?: {
    includeSidecarButtons?: boolean
    maxLineButtons?: number
  },
): boolean {
  const includeSidecarButtons = options?.includeSidecarButtons ?? true
  const normalizedMaxLineButtons =
    options?.maxLineButtons != null &&
    Number.isFinite(options.maxLineButtons) &&
    options.maxLineButtons > 0
      ? options.maxLineButtons
      : null
  const presenceCounts = new Map<number, number>()
  const lineCounts = new Map<number, number>()

  for (const button of source) {
    if (
      normalizedMaxLineButtons != null &&
      button.type === 'LineButton' &&
      button.display_count > normalizedMaxLineButtons
    ) {
      continue
    }
    if (!includeSidecarButtons && button.type === 'SidecarButton') continue

    const buttonFunction = button.button_function?.trim()
    if (!buttonFunction) continue

    const isPrimaryLine =
      buttonFunction === 'line' &&
      button.type === 'LineButton' &&
      button.display_count === 1
    const requiresExplicitExtension =
      BUTTON_FUNCTIONS_USING_EXTENSION.has(buttonFunction) && !isPrimaryLine
    const extensionId = button.extension

    if (requiresExplicitExtension && !extensionId) return true

    if (!extensionId) continue
    if (buttonFunction === 'presence') {
      presenceCounts.set(
        extensionId,
        (presenceCounts.get(extensionId) ?? 0) + 1,
      )
    }
    if (buttonFunction === 'line') {
      lineCounts.set(extensionId, (lineCounts.get(extensionId) ?? 0) + 1)
    }
  }

  return (
    [...presenceCounts.values()].some((count) => count > 1) ||
    [...lineCounts.values()].some((count) => count > 1)
  )
}
